import angular from 'angular';
import _ from 'lodash-es';
import { createNumberMask } from 'text-mask-addons';
import Utils from '~/scripts/services/utils';
import kformUtils from '../../utils/kform-utils';
import template from './currency.html';
import APIV5 from '~/models/api';
import './currency.scss'

const currencyConfig = /* @ngInject */ (/** @type {any} */ KformComponentsProvider) => {
	KformComponentsProvider.register('currency', {
		title:      'Currency',
		template,
		group:      'advanced',
		tableView:  (/** @type {any} */ data, /** @type {any} */ options) => {
			if (_.isNil(data)) {
				return '';
			}

			const separators = Utils.getNumberSeparators();
			const { component } = options;
			const decimalLimit = component.decimalLimit || 2;
			// eslint-disable-next-line unicorn/consistent-destructuring
			const affixes = Utils.getCurrencyAffixes({
				currency:         component.currency,
				decimalLimit,
				decimalSeparator: separators.thousandsSeparator,
			});

			if (!component.delimiter) {
				separators.radix = '';
			}

			// eslint-disable-next-line unicorn/consistent-destructuring
			return options.util.formatNumber(data, createNumberMask({
				prefix:                   affixes.prefix,
				suffix:                   affixes.suffix,
				delimiter:                _.get(component, 'thousandsSeparator', separators.radix),
				decimalSymbol:            _.get(component, 'decimalSymbol', separators.thousandsSeparator),
				decimalLimit,
				allowNegative:            _.get(component, 'allowNegative', true),
				allowDecimal:             _.get(component, 'allowDecimal', true),
			}));
		},
		controller: /* @ngInject */ (
			/** @type {any} */ $scope,
			/** @type {import('angular').ITimeoutService} */ $timeout,
		) => {
			$scope.repreCurrencies = {};
			$scope.displaySymbol = '';

			$timeout(async () => {
				if ($scope.component.kdata.currenciesAvailable && $scope.component.kdata.currenciesAvailable.length === 1) {
					$scope.displaySymbol = await getCurrencySymbol($scope.component.kdata.currenciesAvailable[0].kid);
				}
			});

			if (typeof $scope.data[$scope.component.key] !== 'object' || !('qty' in $scope.data[$scope.component.key])) {
				$timeout(() => {
					$scope.data[$scope.component.key] = {
						qty: $scope.data[$scope.component.key],
						unit: $scope.component.kdata.unit ?? 'K_NODE;EURO',
					};
				});
			}
			const updateComponent = () => {
				if ($scope.component.suffix) {
					delete $scope.component.suffix;
				}
				$scope.maskOptions = {
					padFractionalZeros: $scope.component.requireDecimal,
					scale:              $scope.component.decimalLimit,
				};
			};

			$scope.$watch('component', (/** @type {any} */ newComponent, /** @type {any} */ oldComponent) => {
				if (!_.isEqual(newComponent, oldComponent)) {
					updateComponent();
				}
			}, true);

			updateComponent();

			if ($scope.options?.building) {
				return;
			}

			$scope.$watch('data[component.key].unit', async (
				/** @type {import('@knitiv/api-client-javascript').KNode | undefined} */ newUnit,
			) => {
				if (newUnit) {
					$scope.displaySymbol = await getCurrencySymbol(newUnit);
				}
			});

			const getCurrencySymbol = async (/** @type {import('@knitiv/api-client-javascript').KNode} */ unitKid) => {
				if (unitKid) {
					if (unitKid in $scope.repreCurrencies) {
						return $scope.repreCurrencies[unitKid];
					}
					$scope.repreCurrencies[unitKid] = unitKid;
					const repreGetResult = await APIV5.instance.objectRepreGet(
						{ items: [unitKid], lang_kid: 'K_NODE;SYMBOL' },
						{ id: 'currency-config.get-currency-symbol'},
					);
					$scope.repreCurrencies[unitKid] = repreGetResult.result[unitKid];
					$timeout(() => $scope.$apply());
					return $scope.repreCurrencies[unitKid];
				}
				return '';
			};

			$scope.changeValue = () => {
				if (typeof $scope.component.event.onChange === 'string') {
					const custom = ''.concat($scope.component.event.onChange, '; ');
					kformUtils.evaluate(custom, {
						data: $scope.data,
						form: $scope.form,
					});
				}
			};
		},
		onEdit: /* @ngInject */ (/** @type {any} */ $scope) => {
			if ($scope.component.kdata.currenciesAvailable === undefined) {
				$scope.component.kdata.currenciesAvailable = [];
			}
			$scope.currenciesAvailable = $scope.component.kdata.currenciesAvailable.map(
				(/** @type {import('@knitiv/api-client-javascript').KNode} */ c) => ({kid: c}),
			);

			$scope.updateCurrenciesAvailable = () => {
				$scope.component.kdata.currenciesAvailable = $scope.component.kdata.currenciesAvailable
					.map((/** @type {any} */ c) => ({
						kid: c.kid,
						name: c.name,
					}));
			};
		},
		icon:          'fa fa-usd',
		views:         [
			{
				name:     'Display',
				template: 'kform/components/currency/display.html',
			},
			{
				name:     'Data',
				template: 'kform/components/currency/data.html',
			},
			{
				name:     'Validation',
				template: 'kform/components/currency/validate.html',
			},
			{
				name:     'API',
				template: 'kform/components/common/api.html',
			},
			{
				name:     'Layout',
				template: 'kform/components/common/layout.html',
			},
			{
				name:     'Conditional',
				template: 'kform/components/common/conditional.html',
			},
			{
				name:     'Events',
				template: 'kform/components/common/events.html',
			},
		],
		children:   false,
		selectable: true,
		settings:   {
			autofocus:      false,
			disabled:       false,
			input:          true,
			tableView:      true,
			inputType:      'text',
			inputMask:      '',
			label:          'Currency',
			key:            'currency',
			placeholder:    '',
			prefix:         '',
			defaultValue:   '',
			protected:      false,
			persistent:     true,
			hidden:         false,
			clearOnHide:    true,
			delimiter:      true,
			decimalLimit:   2,
			requireDecimal: true,
			disableUnit:    false,
			hideUnit:       false,
			validate:       {
				card:     '0-1',
				custom:   '',
				min:      '',
				max:      '',
			},
			conditional:    {
				show: null,
				when: null,
				eq:   '',
			},
			event:          {
				onChange: null,
			},
			kdata: {
				currenciesAvailable: [],
				submit: true,
				unit: null,
			},
		},
	});
};

export default angular.module('app').config(currencyConfig);
